import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  CursorArrowRippleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import "./kategorieEinstellungen/builder.css";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { DiscardChangesDialog } from "@/components/ui/dialogCloseGuard";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { useCurrentUser, userHasGroup, userIsRealAppAdmin } from "@/lib/auth";
import {
  getBuilderSchema,
  listFieldTemplates,
  listMaintenanceTypes,
  maintenanceTypeLabel,
  publishForm,
  saveBuilderSchema,
  type FieldTemplate,
  type MaintenanceType,
} from "@/lib/wartungen";
import { fieldsForScope } from "./kategorieEinstellungen/fieldModel";
import { useBuilder } from "./kategorieEinstellungen/useBuilder";
import { BuilderCanvas } from "./kategorieEinstellungen/BuilderCanvas";
import { FieldEditor } from "./kategorieEinstellungen/FieldEditor";
import { GradeConfigPanel } from "./kategorieEinstellungen/GradeConfigPanel";
import { PlanSymbolsSection } from "./kategorieEinstellungen/PlanSymbolsSection";
import {
  collectDefectTriggerIssues,
  formatPublishApiIssues,
  type PublishIssue,
} from "./kategorieEinstellungen/publishValidation";
import { TemplatePalette, buildPaletteItems } from "./kategorieEinstellungen/TemplatePalette";
import { exportMaintenanceForm } from "@/pages/wartungen/lib/formExportImport";

function resolveCategory(types: MaintenanceType[], requested: string): MaintenanceType | undefined {
  const norm = requested.trim().toLowerCase();
  return (
    types.find((t) => String(t.key || "").trim() === requested) ||
    types.find((t) => String(t.key || "").trim().toLowerCase() === norm) ||
    types.find((t) => maintenanceTypeLabel(t).trim().toLowerCase() === norm)
  );
}

export function KategorieEinstellungenPage() {
  const [params] = useSearchParams();
  const requestedCategory = (params.get("kategorie") || "").trim();
  const formId = (params.get("form_id") || "").trim();
  const { user } = useCurrentUser();
  const isAdmin = userIsRealAppAdmin(user) || userHasGroup(user, "App-Admins");

  const builder = useBuilder(formId);
  const {
    fields,
    grade,
    editingUid,
    setEditingUid,
    selectedUid,
    setSelectedUid,
    isDirty,
    dirtyUids,
    loadFromSchema,
    replaceField,
    deleteField,
    resetField,
    dropTemplate,
    moveField,
    toggleGrade,
    buildSavePayload,
  } = builder;

  const [categoryName, setCategoryName] = useState(requestedCategory);
  const [categoryKey, setCategoryKey] = useState(requestedCategory);
  const [maintenanceTypes, setMaintenanceTypes] = useState<MaintenanceType[]>([]);
  const [templates, setTemplates] = useState<FieldTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorItems, setErrorItems] = useState<PublishIssue[] | null>(null);
  const [autoCompletedNote, setAutoCompletedNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reloadConfirmOpen, setReloadConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const reloadSchema = useCallback(async () => {
    if (!formId) return;
    const schema = await getBuilderSchema(formId);
    const { autoCompleted } = loadFromSchema(schema);
    if (autoCompleted.length) {
      const names = autoCompleted.slice(0, 3).map((f) => `„${f.label}“`).join(", ");
      const rest = autoCompleted.length > 3 ? ` und ${autoCompleted.length - 3} weitere` : "";
      setAutoCompletedNote(
        `${autoCompleted.length === 1 ? "Bei einem Mangel-Feld" : `Bei ${autoCompleted.length} Mangel-Feldern`} fehlte die gespeicherte Mangel-Regel. ` +
          `Sie wurde mit dem angezeigten Standard („Ja“ = Mangel) ergänzt: ${names}${rest}. ` +
          "Bitte prüfen und „Felder speichern“ klicken.",
      );
    } else {
      setAutoCompletedNote(null);
    }
  }, [formId, loadFromSchema]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [types, tpls] = await Promise.all([
        listMaintenanceTypes().catch(() => [] as MaintenanceType[]),
        listFieldTemplates().catch(() => [] as FieldTemplate[]),
      ]);
      setMaintenanceTypes(types);
      setTemplates(tpls);
      const active = resolveCategory(types, requestedCategory);
      setCategoryKey(active ? String(active.key) : requestedCategory);
      setCategoryName(active ? maintenanceTypeLabel(active) : requestedCategory);
      if (formId) {
        await reloadSchema();
      } else {
        toast.error("Formular fehlt in der URL (?form_id=...).");
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [formId, requestedCategory, reloadSchema]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const paletteItems = useMemo(() => buildPaletteItems(templates), [templates]);
  const masterdataFields = useMemo(() => fieldsForScope(fields, "masterdata"), [fields]);
  const inspectionFields = useMemo(() => fieldsForScope(fields, "inspection"), [fields]);

  function clearErrors() {
    setErrorItems(null);
  }

  function showErrors(title: string, items: PublishIssue[]) {
    setErrorItems(items.length ? items : [{ message: title }]);
  }

  function jumpToField(uid: string) {
    setSelectedUid(uid);
    setEditingUid(uid);
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-field-uid="${uid}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }

  async function handleSave() {
    if (!formId) return;
    setSaving(true);
    clearErrors();
    try {
      const payload = buildSavePayload();
      // Eindeutige, nicht-leere Feldschlüssel validieren.
      const keys = payload.fields.map((f) => String(f.field_key || "").trim());
      if (keys.some((k) => !k)) throw new Error("Alle Felder benötigen einen Feldschlüssel.");
      const dupes = keys.filter((k, i) => keys.indexOf(k) !== i);
      if (dupes.length) throw new Error(`Doppelte Feldschlüssel: ${[...new Set(dupes)].join(", ")}`);
      await saveBuilderSchema(formId, payload);
      toast.success("Felder gespeichert.");
      await reloadSchema();
    } catch (err) {
      if (err instanceof ApiError) {
        showErrors("Speichern fehlgeschlagen", formatPublishApiIssues(err, fields));
      } else {
        toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        setErrorItems(null);
      }
    } finally {
      setSaving(false);
    }
  }

  async function performReload() {
    setReloadConfirmOpen(false);
    clearErrors();
    try {
      await reloadSchema();
      toast.success("Felder aktualisiert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Felder konnten nicht aktualisiert werden.");
    }
  }

  function handleReloadClick() {
    if (isDirty) {
      setReloadConfirmOpen(true);
      return;
    }
    void performReload();
  }

  async function handlePublish() {
    if (!formId) return;
    if (isDirty) {
      toast.error("Bitte erst speichern, bevor das Formular publiziert wird.");
      setErrorItems(null);
      return;
    }
    const localIssues = collectDefectTriggerIssues(fields);
    if (localIssues.length) {
      showErrors("Publizieren nicht möglich", localIssues);
      return;
    }
    setPublishing(true);
    clearErrors();
    try {
      await publishForm(formId);
      toast.success("Formular wurde publiziert.");
      await reloadSchema();
    } catch (err) {
      if (err instanceof ApiError) {
        showErrors("Publizieren nicht möglich", formatPublishApiIssues(err, fields));
      } else {
        toast.error("Publizieren fehlgeschlagen.");
        setErrorItems(null);
      }
    } finally {
      setPublishing(false);
    }
  }

  async function handleExport() {
    if (!formId) return;
    setExporting(true);
    try {
      await exportMaintenanceForm({
        formId,
        formName: categoryName ? `${categoryName} #${formId}` : `Formular #${formId}`,
        categoryKey,
      });
      toast.success("Formular wurde exportiert.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Formular konnte nicht exportiert werden.");
    } finally {
      setExporting(false);
    }
  }

  function finishFieldEditing(uid: string) {
    setEditingUid("");
    setSelectedUid(uid);
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-field-uid="${uid}"]`)
        ?.scrollIntoView({ block: "nearest", behavior: "instant" });
    });
  }

  const editorRenderer = (field: (typeof fields)[number]) => (
    <FieldEditor
      field={field}
      allFields={fields}
      enabledGrades={grade.enabled_grades}
      maintenanceTypes={maintenanceTypes}
      onApply={(updated) => {
        replaceField(updated);
        finishFieldEditing(field.uid);
      }}
      onCancel={() => finishFieldEditing(field.uid)}
    />
  );

  return (
    <AppShell
      title="Feld-Konfiguration"
      subtitle={categoryName ? `Kategorie: ${categoryName}${formId ? ` · Formular #${formId}` : ""}` : undefined}
      back={
        requestedCategory
          ? { to: `/wartungen/kategorie?kategorie=${encodeURIComponent(requestedCategory)}`, label: "zur Kategorie" }
          : { to: "/wartungen/einstellungen", label: "zu Kategorien" }
      }
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {isDirty && !loading ? (
            <span className="mb-dirty-pill" role="status">
              Ungespeicherte Änderungen
            </span>
          ) : null}
          <Button type="button" variant="secondary" onClick={handleReloadClick} disabled={loading}>
            Felder aktualisieren
          </Button>
          <Button type="button" variant="secondary" loading={exporting} disabled={loading || !formId} onClick={() => void handleExport()}>
            <ArrowDownTrayIcon className="size-4" aria-hidden />
            Exportieren
          </Button>
          <Button type="button" loading={saving} disabled={!isDirty || loading} onClick={() => void handleSave()}>
            Felder speichern
          </Button>
          {isAdmin ? (
            <Button type="button" variant="warning" loading={publishing} disabled={loading} onClick={() => void handlePublish()}>
              Publizieren
            </Button>
          ) : null}
        </div>
      }
    >
      {errorItems?.length ? (
        <Alert variant="error" title="Konfiguration unvollständig" className="mb-4">
          <p className="mt-1 text-xs opacity-80">
            {errorItems.length === 1 ? "1 Punkt" : `${errorItems.length} Punkte`} müssen behoben werden. Ein Klick auf einen Eintrag öffnet das betroffene Feld.
          </p>
          <ul className="mb-issue-list mt-2">
            {errorItems.map((item, index) => (
              <li key={`${item.fieldUid || ""}-${index}`}>
                {item.fieldUid ? (
                  <button type="button" className="mb-issue-link" onClick={() => jumpToField(item.fieldUid!)}>
                    {item.message}
                  </button>
                ) : (
                  <span>{item.message}</span>
                )}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {autoCompletedNote && !loading ? (
        <Alert variant="info" title="Mangel-Regeln automatisch ergänzt" className="mb-4">
          {autoCompletedNote}
        </Alert>
      ) : null}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lade Formular …
        </p>
      ) : !formId ? (
        <Alert variant="warning">Es wurde kein Formular ausgewählt.</Alert>
      ) : (
        <div className="space-y-8">
          {/* ── Kurzanleitung ── */}
          <div className="mb-howto" role="note">
            <div className="mb-howto__step">
              <span className="mb-howto__icon" aria-hidden><CursorArrowRippleIcon /></span>
              <div>
                <strong>1 · Feld hinzufügen</strong>
                <span>Aus der Bibliothek rechts ziehen oder auf „+“ klicken.</span>
              </div>
            </div>
            <div className="mb-howto__step">
              <span className="mb-howto__icon" aria-hidden><PencilSquareIcon /></span>
              <div>
                <strong>2 · Feld anpassen</strong>
                <span>Stift-Symbol öffnet Bezeichnung, Optionen &amp; Regeln.</span>
              </div>
            </div>
            <div className="mb-howto__step">
              <span className="mb-howto__icon" aria-hidden><CheckBadgeIcon /></span>
              <div>
                <strong>3 · Speichern</strong>
                <span>„Felder speichern“ – fertig ist das Wartungsformular.</span>
              </div>
            </div>
          </div>

          {/* ── Stammdaten-Felder ── */}
          <section className="mb-builder-card rounded-ios border border-separator bg-card shadow-ios">
            <header className="mb-builder-card__header border-b border-separator px-5 py-4">
              <div className="mb-builder-card__heading">
                <span className="mb-step-badge" aria-hidden>1</span>
                <div>
                  <h2 className="text-base font-semibold text-label">
                    Stammdaten
                    <span className="mb-count-badge">{masterdataFields.length} Felder</span>
                  </h2>
                  <p className="mt-0.5 text-sm text-label-secondary">
                    Eigenschaften des Wartungsobjekts, z. B. Hersteller oder Baujahr – werden einmalig erfasst.
                  </p>
                </div>
              </div>
            </header>
            <div className="px-5 py-4">
              <div className="mb-builder-layout">
                <BuilderCanvas
                  scope="masterdata"
                  fields={fields}
                  paletteItems={paletteItems}
                  editingUid={editingUid}
                  selectedUid={selectedUid}
                  dirtyUids={dirtyUids}
                  onDropTemplate={dropTemplate}
                  onMoveField={moveField}
                  onSelect={setSelectedUid}
                  onEdit={(uid) => setEditingUid((cur) => (cur === uid ? "" : uid))}
                  onReset={resetField}
                  onDelete={deleteField}
                  renderEditor={editorRenderer}
                  emptyHint="Noch keine Stammdatenfelder."
                />
                <TemplatePalette
                  title="Feld-Bibliothek"
                  description="Ziehen oder per „+“ hinzufügen."
                  apiTemplates={templates}
                  scope="masterdata"
                  onAdd={(tpl) => dropTemplate(tpl, "masterdata", null)}
                />
              </div>
            </div>
          </section>

          {/* ── Wartungsformular-Felder ── */}
          <section className="mb-builder-card rounded-ios border border-separator bg-card shadow-ios">
            <header className="mb-builder-card__header border-b border-separator px-5 py-4">
              <div className="mb-builder-card__heading">
                <span className="mb-step-badge" aria-hidden>2</span>
                <div>
                  <h2 className="text-base font-semibold text-label">
                    Wartungsformular
                    <span className="mb-count-badge">{inspectionFields.length} Felder</span>
                  </h2>
                  <p className="mt-0.5 text-sm text-label-secondary">
                    Diese Felder füllt das Wartungsteam bei jeder Wartung aus.
                  </p>
                </div>
              </div>
            </header>
            <div className="space-y-4 px-5 py-4">
              <GradeConfigPanel enabledGrades={grade.enabled_grades} onToggle={toggleGrade} />
              <div className="mb-builder-layout">
                <BuilderCanvas
                  scope="inspection"
                  fields={fields}
                  paletteItems={paletteItems}
                  editingUid={editingUid}
                  selectedUid={selectedUid}
                  dirtyUids={dirtyUids}
                  onDropTemplate={dropTemplate}
                  onMoveField={moveField}
                  onSelect={setSelectedUid}
                  onEdit={(uid) => setEditingUid((cur) => (cur === uid ? "" : uid))}
                  onReset={resetField}
                  onDelete={deleteField}
                  renderEditor={editorRenderer}
                  emptyHint="Noch keine Wartungsformular-Felder."
                />
                <TemplatePalette
                  title="Feld-Bibliothek"
                  description="Ziehen oder per „+“ hinzufügen."
                  apiTemplates={templates}
                  scope="inspection"
                  onAdd={(tpl) => dropTemplate(tpl, "inspection", null)}
                />
              </div>
            </div>
          </section>

          {/* ── Plansymbole (nur Admin) ── */}
          {isAdmin && categoryKey ? (
            <PlanSymbolsSection
              categoryKey={categoryKey}
              masterdataFields={masterdataFields}
              templates={templates}
            />
          ) : null}
        </div>
      )}

      <DiscardChangesDialog
        open={reloadConfirmOpen}
        onCancel={() => setReloadConfirmOpen(false)}
        onConfirm={() => void performReload()}
        title="Ungespeicherte Änderungen"
        description="Beim Aktualisieren gehen nicht gespeicherte Änderungen verloren."
        body="Möchten Sie die Felder trotzdem neu laden?"
        confirmLabel="Neu laden"
      />
    </AppShell>
  );
}
